import { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';
import { useTable, useSortBy, useFilters, usePagination, useFlexLayout } from 'react-table';
import { Table, HeaderRow, Cell, TotalRow } from './Base';
import { HeaderCell, DeleteHeaderCell } from './HeaderCell';
import { H3 } from '../../base/typography/index';
import { Pagination } from '../../ui_elements';
import { Toggleable } from '..';
import { colors } from '../../styles/theme';
import Inflector from '../../utils/inflector';
import useDeletableRows from './useDeletableRows';
import useControllableTableConsumer, {
  getControlledTableProps,
} from './useControllableTableConsumer';
import Row from './Row';

export default function DataTable(props) {
  const {
    columns,
    data,
    pageSize,
    recordNoun,
    onControlsChange,
    serverPagination,
    showTotal,
    pageCount,
    totalRecordCount,
    showDelete,
    onDeleteRow,
    defaultSortBy,
    defaultFilters,
    defaultPageIndex,
    errorMessage,
  } = props;

  const ErrorMessageText = styled(H3)`
    color: ${colors.grey.primaryLight};
    font-size: 24px;
    margin-bottom: 7px;
  `;

  const deleteState = useDeletableRows(showDelete, onDeleteRow);

  const tableInstance = useTable(
    {
      columns,
      data,
      ...getControlledTableProps(serverPagination),
      initialState: {
        pageSize,
        sortBy: defaultSortBy,
        filters: defaultFilters,
        pageIndex: defaultPageIndex,
      },
      pageCount,
    },
    useFilters,
    useSortBy,
    usePagination,
    useFlexLayout
  );

  useControllableTableConsumer(serverPagination, onControlsChange, tableInstance);

  const {
    getTableProps,
    headerGroups,
    rows,
    state: { pageIndex, filters },
    page,
    pageCount: instancePageCount,
    gotoPage,
    prepareRow,
  } = tableInstance;

  const recordNounPluralized = new Inflector().pluralize(recordNoun || 'record', rows.length, {
    showNumber: false,
  });

  const handlePageChange = useCallback(
    (evt) => {
      gotoPage(evt.target.dataset.page - 1);
    },
    [gotoPage]
  );

  if (data.length) {
    return (
      <div>
        <Table {...getTableProps()} showDelete={showDelete}>
          <TotalRow>
            {showTotal && (
              <Cell>
                {serverPagination ? totalRecordCount : rows.length} Total {recordNounPluralized}
              </Cell>
            )}
          </TotalRow>

          {headerGroups.map((headerGroup) => (
            <HeaderRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <HeaderCell column={column} data={data} filters={filters} key={column.id} />
              ))}

              <Toggleable show={showDelete}>
                <DeleteHeaderCell />
              </Toggleable>
            </HeaderRow>
          ))}

          {page &&
            page.length &&
            page.map((row, i) => (
              <Row
                key={row.id}
                row={row}
                index={i}
                deleteState={deleteState}
                prepareRow={prepareRow}
              />
            ))}

          {instancePageCount > 1 && (
            <Pagination
              handlePageChange={handlePageChange}
              currentPage={pageIndex + 1}
              numPages={instancePageCount}
            />
          )}
        </Table>
      </div>
    );
  }
  return <ErrorMessageText>{errorMessage}</ErrorMessageText>;
}

DataTable.defaultProps = {
  recordNoun: '',
  pageSize: 10,
  onControlsChange: () => {},
  serverPagination: false,
  showTotal: true,
  pageCount: 10, // For some reason this needs to be > 1 otherwise it takes two clicks to gotoPage()
  totalRecordCount: 0,
  onDeleteRow: () => {},
  showDelete: false,
  defaultSortBy: [],
  defaultFilters: [],
  defaultPageIndex: 0,
  errorMessage: '',
};

DataTable.propTypes = {
   
  columns: PropTypes.any.isRequired,
   
  data: PropTypes.any.isRequired,
  recordNoun: PropTypes.string,
  pageSize: PropTypes.number,
  onControlsChange: PropTypes.func,
  serverPagination: PropTypes.bool,
  showTotal: PropTypes.bool,
  pageCount: PropTypes.number,
  totalRecordCount: PropTypes.number,
  onDeleteRow: PropTypes.func,
  showDelete: PropTypes.bool,
  defaultSortBy: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, desc: PropTypes.bool })),
   
  defaultFilters: PropTypes.array,
  defaultPageIndex: PropTypes.number,
  errorMessage: PropTypes.string,
};
